import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "./api";

const AuthContext = createContext(null);

const ELEVATED = new Set(["owner", "admin", "cfo", "partner"]);

// Boot-time fallback set. Used only if `/api/config` fetch hasn't completed
// yet (or fails outright — e.g. offline). The authoritative list lives in
// `backend/core/rbac.py` (env var `SUPER_OWNER_EMAILS`); the frontend hydrates
// it via `/api/config` on every auth refresh so adding a new super-owner is
// a single-env-var change with a backend restart — no frontend rebuild.
const FALLBACK_SUPER_OWNER_EMAILS = new Set([
  "strategicrelationsnevada@gmail.com",
  "admin@reup.app",
  "harveypanesar@gmail.com",
  "sukhinder.k@gmail.com",
]);

// Landed/Master cost visibility — mirrors COST_VISIBLE_EMAILS in
// backend/core/rbac.py. Hydrated from /api/config; this is only the fallback.
const FALLBACK_COST_VISIBLE_EMAILS = new Set([
  "strategicrelationsnevada@gmail.com",
  "harveypanesar@gmail.com",
  "sukhinder.k@gmail.com",
]);

export function AuthProvider({ children }) {
  // null = checking, false = unauth, object = user
  const [user, setUser] = useState(null);
  const [superOwnerEmails, setSuperOwnerEmails] = useState(FALLBACK_SUPER_OWNER_EMAILS);
  const [costVisibleEmails, setCostVisibleEmails] = useState(FALLBACK_COST_VISIBLE_EMAILS);
  // Track in-flight config fetch so concurrent refresh() calls don't stampede.
  const configLoaded = useRef(false);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/config");
      const list = Array.isArray(data?.super_owner_emails) ? data.super_owner_emails : [];
      if (list.length > 0) {
        setSuperOwnerEmails(new Set(list.map((e) => (e || "").toLowerCase())));
      }
      const costList = Array.isArray(data?.cost_visible_emails) ? data.cost_visible_emails : [];
      if (costList.length > 0) {
        setCostVisibleEmails(new Set(costList.map((e) => (e || "").toLowerCase())));
      }
      configLoaded.current = true;
    } catch {
      // Fall back silently — the hardcoded set keeps the app usable if /config
      // is unreachable. Don't retry aggressively; next auth event will retry.
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadConfig();
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, [loadConfig]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    loadConfig();
    return data;
  };

  const register = async ({ name, email, password, invite_token }) => {
    const { data } = await api.post("/auth/register", { name, email, password, invite_token });
    setUser(data);
    loadConfig();
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Silent: server-side session may already be gone; clear client state regardless.
    }
    setUser(false);
  };

  const isElevated = user && user !== false && ELEVATED.has(user.role);
  const isOwner = user && user !== false && (user.role === "owner" || user.role === "admin");
  const isDistributor = user && user !== false && user.role === "distributor";
  const isSuperOwner =
    user && user !== false && superOwnerEmails.has((user.email || "").toLowerCase());
  const canSeeCost =
    user &&
    user !== false &&
    !user.hide_landed_cost &&
    costVisibleEmails.has((user.email || "").toLowerCase());
  const mustChangePassword = !!(user && user !== false && user.must_change_password);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refresh, isElevated, isOwner, isDistributor, isSuperOwner, canSeeCost, mustChangePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { ELEVATED };
